import React, { useEffect, useState } from "react";
import { X, Loader2, Check, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Modal for the "Enquire to buy" flow on enquire-only products.
 *
 * Props:
 *   - product: full product object (has variants[])
 *   - initialVariantId: variant to pre-select (usually the one visible
 *     on the detail page's variant picker)
 *   - onClose: fired when the modal is dismissed
 *
 * Submits to POST /api/merch-enquiries. Backend re-validates
 * product+variant and rate-limits per-email. Success flips to a
 * confirmation state; the customer closes the modal manually.
 */
export default function MerchEnquireModal({ product, initialVariantId, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [variantId, setVariantId] = useState(
    initialVariantId || product?.variants?.[0]?.id || ""
  );
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const variant = product?.variants?.find((v) => v.id === variantId);
  const canSubmit =
    !submitting &&
    !done &&
    name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(email) &&
    !!variantId &&
    quantity >= 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/merch-enquiries", {
        name: name.trim(),
        email: email.trim(),
        product_id: product.id,
        variant_id: variantId,
        quantity: Number(quantity) || 1,
        message: message.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="merch-enquire-modal"
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-md border border-white bg-white text-[var(--brand-ink)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white hover:border-[#0A0F17]"
          data-testid="merch-enquire-close"
        >
          <X size={14} />
        </button>

        {done ? (
          <div className="p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Check size={22} />
            </div>
            <h2 className="mt-4 font-display text-xl font-black">Enquiry sent.</h2>
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              Thanks {name.split(" ")[0] || "for reaching out"} — we&apos;ll reply to{" "}
              <strong>{email}</strong> shortly with a payment link and confirm the order.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:opacity-90"
              data-testid="merch-enquire-done"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col">
            <div className="border-b border-black/10 bg-[#F8FAFC] p-5">
              <p className="label-kicker text-[#6CABDD]">Enquire to buy</p>
              <h2 className="mt-1 font-display text-xl font-black leading-tight">
                {product.title}
              </h2>
              <p className="mt-1 text-xs text-[var(--brand-body)]">
                Send a quick message and we&apos;ll reply with a payment link. No account
                needed.
              </p>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="label-kicker text-[var(--brand-body)]">Your name</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={120}
                    className="mt-1 w-full rounded-sm border border-black/15 bg-white px-3 py-2 outline-none focus:border-[#0A0F17]"
                    data-testid="merch-enquire-name"
                  />
                </label>
                <label className="block text-sm">
                  <span className="label-kicker text-[var(--brand-body)]">Your email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    maxLength={254}
                    className="mt-1 w-full rounded-sm border border-black/15 bg-white px-3 py-2 outline-none focus:border-[#0A0F17]"
                    data-testid="merch-enquire-email"
                  />
                </label>
              </div>

              {product.variants?.length > 1 ? (
                <label className="block text-sm">
                  <span className="label-kicker text-[var(--brand-body)]">Option</span>
                  <select
                    value={variantId}
                    onChange={(e) => setVariantId(e.target.value)}
                    className="mt-1 w-full rounded-sm border border-black/15 bg-white px-3 py-2 outline-none focus:border-[#0A0F17]"
                    data-testid="merch-enquire-variant"
                  >
                    {product.variants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label} — £{Number(v.price).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : variant ? (
                <div
                  className="rounded-md border border-black/10 bg-[#F8FAFC] px-3 py-2 text-sm"
                  data-testid="merch-enquire-variant-single"
                >
                  <span className="label-kicker text-[var(--brand-body)]">Option</span>
                  <div>
                    {variant.label} — £{Number(variant.price).toFixed(2)}
                  </div>
                </div>
              ) : null}

              <label className="block text-sm">
                <span className="label-kicker text-[var(--brand-body)]">Quantity</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))
                  }
                  className="mt-1 w-24 rounded-sm border border-black/15 bg-white px-3 py-2 outline-none focus:border-[#0A0F17]"
                  data-testid="merch-enquire-quantity"
                />
              </label>

              <label className="block text-sm">
                <span className="label-kicker text-[var(--brand-body)]">
                  Message (optional)
                </span>
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={2000}
                  placeholder="Delivery city, preferred colour, questions…"
                  className="mt-1 w-full rounded-sm border border-black/15 bg-white px-3 py-2 outline-none focus:border-[#0A0F17]"
                  data-testid="merch-enquire-message"
                />
              </label>

              {error ? (
                <p
                  className="rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
                  data-testid="merch-enquire-error"
                >
                  {error}
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-between border-t border-black/10 bg-[#F8FAFC] p-5">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-[var(--brand-body)] hover:text-[var(--brand-ink)] hover:underline"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                data-testid="merch-enquire-submit"
              >
                {submitting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {submitting ? "Sending…" : "Send enquiry"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
